import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../components/ui/table';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Pencil, Trash2, FileText, Calendar, User, 
  Stethoscope, Pill, DollarSign, Image, Download, Filter,
  ChevronLeft, ChevronRight, Settings, Hash, Eye, AlertTriangle,
  BarChart3, Upload, Printer, Check, ChevronsUpDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrescriptionRegisterPage = () => {
  const { currentOrg, user } = useAuth();
  const [activeTab, setActiveTab] = useState('register');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [physicians, setPhysicians] = useState([]);
  const [patients, setPatients] = useState([]);
  const [controlledItems, setControlledItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  // Monthly Report state
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  
  // Combobox open states
  const [physicianOpen, setPhysicianOpen] = useState(false);
  const [patientOpen, setPatientOpen] = useState(false);
  const [medicationOpen, setMedicationOpen] = useState(false);
  
  // Quick add dialogs
  const [showQuickAddPhysician, setShowQuickAddPhysician] = useState(false);
  const [showQuickAddPatient, setShowQuickAddPatient] = useState(false);
  const [quickPhysicianName, setQuickPhysicianName] = useState('');
  const [quickPatientName, setQuickPatientName] = useState('');
  
  // Filters
  const [filterPhysician, setFilterPhysician] = useState('');
  const [filterPatient, setFilterPatient] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [viewingPrescription, setViewingPrescription] = useState(null);
  const [nextSerialNumber, setNextSerialNumber] = useState(1);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    physician_id: '',
    patient_id: '',
    medication_id: '',
    medication_name: '',
    instructions: '',
    dosage_gram: '',
    dosage_cent: '',
    dosage_mill: '',
    dosage_gtes: '',
    quantity: 1,
    treatment_length_months: 1,
    prescription_image_url: '',
    notes: ''
  });
  
  // Calculate expected pickup window based on date and treatment length
  const calculatePickupWindow = (date, months) => {
    if (!date || !months) return null;
    const baseDate = new Date(date);
    const expectedDate = new Date(baseDate);
    expectedDate.setMonth(expectedDate.getMonth() + months);
    
    const windowStart = new Date(expectedDate);
    windowStart.setDate(windowStart.getDate() - 4);
    
    const windowEnd = new Date(expectedDate);
    windowEnd.setDate(windowEnd.getDate() + 4);
    
    return {
      expected: expectedDate.toISOString().split('T')[0],
      start: windowStart.toISOString().split('T')[0],
      end: windowEnd.toISOString().split('T')[0]
    };
  };
  
  // Get pickup status based on current date
  const getPickupStatus = (prescription) => {
    if (!prescription.pickup_window_start || !prescription.pickup_window_end) {
      return { status: 'unknown', color: 'gray' };
    }
    
    const today = new Date().toISOString().split('T')[0];
    const windowStart = prescription.pickup_window_start;
    const windowEnd = prescription.pickup_window_end;
    
    if (today > windowEnd) {
      const daysOverdue = Math.floor((new Date(today) - new Date(windowEnd)) / (1000 * 60 * 60 * 24));
      return { status: 'overdue', color: 'red', daysOverdue };
    } else if (today >= windowStart && today <= windowEnd) {
      return { status: 'due', color: 'green' };
    } else {
      const daysUntil = Math.floor((new Date(windowStart) - new Date(today)) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 7) {
        return { status: 'upcoming', color: 'yellow', daysUntil };
      }
      return { status: 'scheduled', color: 'blue', daysUntil };
    }
  };

  // Fetch all data
  const fetchData = async () => {
    if (!currentOrg?.id) return;
    try {
      const [physRes, patRes, itemsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/physicians?organization_id=${currentOrg.id}`),
        axios.get(`${API}/patients?organization_id=${currentOrg.id}`),
        axios.get(`${API}/prescription-register/controlled-items?organization_id=${currentOrg.id}`),
        axios.get(`${API}/prescription-register/settings?organization_id=${currentOrg.id}`)
      ]);
      setPhysicians(physRes.data || []);
      setPatients(patRes.data || []);
      setControlledItems(itemsRes.data || []);
      setNextSerialNumber(settingsRes.data?.next_serial_number || 1);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const fetchPrescriptions = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        organization_id: currentOrg.id,
        page: page.toString(),
        page_size: '50'
      });
      if (search) params.set('search', search);
      if (filterPhysician) params.set('physician_id', filterPhysician);
      if (filterPatient) params.set('patient_id', filterPatient);
      if (filterFromDate) params.set('from_date', filterFromDate);
      if (filterToDate) params.set('to_date', filterToDate);
      
      const res = await axios.get(`${API}/prescription-register?${params}`);
      setPrescriptions(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch prescriptions:', error);
      toast.error('Failed to load prescription register');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentOrg?.id]);

  useEffect(() => {
    fetchPrescriptions();
  }, [currentOrg?.id, page, search, filterPhysician, filterPatient, filterFromDate, filterToDate]);

  // Fetch alerts when tab changes to alerts
  const fetchAlerts = async () => {
    if (!currentOrg?.id) return;
    setAlertsLoading(true);
    try {
      const res = await axios.get(`${API}/prescription-register/alerts?organization_id=${currentOrg.id}&days=30`);
      setAlerts(res.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Fetch monthly report
  const fetchMonthlyReport = async () => {
    if (!currentOrg?.id) return;
    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/prescription-register/report/monthly?organization_id=${currentOrg.id}&year=${reportYear}&month=${reportMonth}`);
      setMonthlyReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error('Failed to load monthly report');
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'alerts') {
      fetchAlerts();
    }
  }, [activeTab, currentOrg?.id]);

  // Quick add physician
  const handleQuickAddPhysician = async () => {
    if (!quickPhysicianName.trim()) {
      toast.error('Please enter physician name');
      return;
    }
    try {
      const res = await axios.post(`${API}/physicians`, {
        organization_id: currentOrg.id,
        name: quickPhysicianName.trim()
      });
      setPhysicians(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, physician_id: res.data.id }));
      setShowQuickAddPhysician(false);
      setQuickPhysicianName('');
      toast.success('Physician added');
    } catch (error) {
      toast.error('Failed to add physician');
    }
  };

  // Quick add patient
  const handleQuickAddPatient = async () => {
    if (!quickPatientName.trim()) {
      toast.error('Please enter patient name');
      return;
    }
    try {
      const res = await axios.post(`${API}/patients`, {
        organization_id: currentOrg.id,
        name: quickPatientName.trim()
      });
      setPatients(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, patient_id: res.data.id }));
      setShowQuickAddPatient(false);
      setQuickPatientName('');
      toast.success('Patient added');
    } catch (error) {
      toast.error('Failed to add patient');
    }
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const res = await axios.post(
        `${API}/prescription-register/upload-image?organization_id=${currentOrg.id}`,
        uploadFormData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData(prev => ({ ...prev, prescription_image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      physician_id: '',
      patient_id: '',
      medication_id: '',
      medication_name: '',
      instructions: '',
      dosage_gram: '',
      dosage_cent: '',
      dosage_mill: '',
      dosage_gtes: '',
      quantity: 1,
      treatment_length_months: 1,
      prescription_image_url: '',
      notes: ''
    });
    setEditingPrescription(null);
  };

  const openAddDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (prescription) => {
    setEditingPrescription(prescription);
    setFormData({
      date: prescription.date || '',
      physician_id: prescription.physician_id || '',
      patient_id: prescription.patient_id || '',
      medication_id: prescription.medication_id || '',
      medication_name: prescription.medication_name || '',
      instructions: prescription.instructions || '',
      dosage_gram: prescription.dosage_gram || '',
      dosage_cent: prescription.dosage_cent || '',
      dosage_mill: prescription.dosage_mill || '',
      dosage_gtes: prescription.dosage_gtes || '',
      quantity: prescription.quantity || 1,
      treatment_length_months: prescription.treatment_length_months || 1,
      prescription_image_url: prescription.prescription_image_url || '',
      notes: prescription.notes || ''
    });
    setShowDialog(true);
  };

  const handleMedicationSelect = (medicationId) => {
    const item = controlledItems.find(i => i.id === medicationId);
    if (item) {
      setFormData(prev => ({
        ...prev,
        medication_id: item.id,
        medication_name: item.name
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.physician_id) {
      toast.error('Please select a physician');
      return;
    }
    if (!formData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    if (!formData.medication_name.trim()) {
      toast.error('Medication name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        organization_id: currentOrg.id,
        dosage_gram: parseFloat(formData.dosage_gram) || 0,
        dosage_cent: parseFloat(formData.dosage_cent) || 0,
        dosage_mill: parseFloat(formData.dosage_mill) || 0,
        dosage_gtes: parseFloat(formData.dosage_gtes) || 0,
        quantity: parseFloat(formData.quantity) || 1,
        treatment_length_months: parseInt(formData.treatment_length_months) || 1
      };

      if (editingPrescription) {
        await axios.put(`${API}/prescription-register/${editingPrescription.id}`, payload);
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/prescription-register`, payload);
        toast.success('Entry added');
        setNextSerialNumber(prev => prev + 1);
      }
      setShowDialog(false);
      resetForm();
      fetchPrescriptions();
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (prescription) => {
    if (!window.confirm(`Delete entry #${prescription.serial_number}?`)) return;
    try {
      await axios.delete(`${API}/prescription-register/${prescription.id}`);
      toast.success('Entry deleted');
      fetchPrescriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const updateSerialNumber = async () => {
    try {
      await axios.put(`${API}/prescription-register/settings?organization_id=${currentOrg.id}&next_serial_number=${nextSerialNumber}`);
      toast.success('Serial number updated');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const printReport = () => {
    window.print();
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Prescription Register
          </h1>
          <p className="text-muted-foreground text-sm">Controlled substance tracking ledger</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button onClick={openAddDialog} data-testid="add-prescription-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Entry
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="register" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Register
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Alerts {alerts.length > 0 && <Badge variant="destructive" className="ml-1 text-[10px] px-1">{alerts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Register Tab */}
        <TabsContent value="register" className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Hash className="w-5 h-5 mx-auto mb-1 text-primary" />
            <div className="text-xl font-bold">{nextSerialNumber}</div>
            <div className="text-[10px] text-muted-foreground">Next Serial #</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <div className="text-xl font-bold">{total}</div>
            <div className="text-[10px] text-muted-foreground">Total Entries</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <User className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <div className="text-xl font-bold">{patients.length}</div>
            <div className="text-[10px] text-muted-foreground">Patients</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Stethoscope className="w-5 h-5 mx-auto mb-1 text-purple-500" />
            <div className="text-xl font-bold">{physicians.length}</div>
            <div className="text-[10px] text-muted-foreground">Physicians</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Pill className="w-5 h-5 mx-auto mb-1 text-orange-500" />
            <div className="text-xl font-bold">{controlledItems.length}</div>
            <div className="text-[10px] text-muted-foreground">Controlled Items</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10"
              />
            </div>
            <Select value={filterPhysician} onValueChange={(v) => { setFilterPhysician(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Physicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Physicians</SelectItem>
                {physicians.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPatient} onValueChange={(v) => { setFilterPatient(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="All Patients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Patients</SelectItem>
                {patients.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filterFromDate}
              onChange={(e) => { setFilterFromDate(e.target.value); setPage(1); }}
              placeholder="From Date"
            />
            <Input
              type="date"
              value={filterToDate}
              onChange={(e) => { setFilterToDate(e.target.value); setPage(1); }}
              placeholder="To Date"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-xs">
                <TableHead className="w-16">Serial #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Physician</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Medication</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead className="text-center">Qty</TableHead>
                <TableHead className="text-center">Treatment</TableHead>
                <TableHead>Pickup Window</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : prescriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No entries found</TableCell>
                </TableRow>
              ) : (
                prescriptions.map(p => {
                  const pickupStatus = getPickupStatus(p);
                  return (
                  <TableRow key={p.id} className="text-xs">
                    <TableCell className="font-mono font-bold text-primary">{p.serial_number}</TableCell>
                    <TableCell>{formatDate(p.date)}</TableCell>
                    <TableCell className="max-w-[100px] truncate">{p.physician_name}</TableCell>
                    <TableCell className="max-w-[100px] truncate">{p.patient_name}</TableCell>
                    <TableCell className="font-medium max-w-[120px] truncate">{p.medication_name}</TableCell>
                    <TableCell className="max-w-[150px] truncate text-muted-foreground" dir="auto">{p.instructions || '-'}</TableCell>
                    <TableCell className="text-center">{p.quantity}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="text-[10px]">{p.treatment_length_months || 1}M</Badge>
                    </TableCell>
                    <TableCell>
                      {p.pickup_schedule && p.pickup_schedule.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {p.pickup_schedule.map((pickup, idx) => {
                            const today = new Date().toISOString().split('T')[0];
                            let status = 'scheduled';
                            let daysInfo = '';
                            
                            if (today > pickup.pickup_window_end) {
                              status = pickup.picked_up ? 'completed' : 'overdue';
                              if (!pickup.picked_up) {
                                const days = Math.floor((new Date(today) - new Date(pickup.pickup_window_end)) / (1000*60*60*24));
                                daysInfo = `${days}d overdue`;
                              }
                            } else if (today >= pickup.pickup_window_start) {
                              status = 'due';
                            } else {
                              const days = Math.floor((new Date(pickup.pickup_window_start) - new Date(today)) / (1000*60*60*24));
                              daysInfo = `In ${days}d`;
                            }
                            
                            return (
                              <div key={idx} className="flex items-center gap-1">
                                <span className="text-[9px] text-muted-foreground w-4">{pickup.month_number}/</span>
                                <span className="text-[9px]">{formatDate(pickup.pickup_window_start)}</span>
                                <Badge 
                                  variant={status === 'overdue' ? 'destructive' : 
                                          status === 'due' ? 'default' : 
                                          status === 'completed' ? 'secondary' : 'outline'}
                                  className="text-[8px] px-1"
                                >
                                  {status === 'completed' && '✓'}
                                  {status === 'overdue' && daysInfo}
                                  {status === 'due' && 'Due'}
                                  {status === 'scheduled' && daysInfo}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      ) : p.pickup_window_start && p.pickup_window_end ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-muted-foreground">
                            {formatDate(p.pickup_window_start)} - {formatDate(p.pickup_window_end)}
                          </span>
                          <Badge 
                            variant={pickupStatus.status === 'overdue' ? 'destructive' : 
                                    pickupStatus.status === 'due' ? 'default' : 
                                    pickupStatus.status === 'upcoming' ? 'secondary' : 'outline'}
                            className="text-[9px] w-fit"
                          >
                            {pickupStatus.status === 'overdue' && `${pickupStatus.daysOverdue}d overdue`}
                            {pickupStatus.status === 'due' && 'Due now'}
                            {pickupStatus.status === 'upcoming' && `In ${pickupStatus.daysUntil}d`}
                            {pickupStatus.status === 'scheduled' && `In ${pickupStatus.daysUntil}d`}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setViewingPrescription(p); setShowViewDialog(true); }}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(p)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        {(user?.role === 'super_admin' || user?.role === 'admin') && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} className="text-red-400">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} ({total} entries)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Prescription Alerts (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No alerts detected</div>
              ) : (
                <div className="space-y-4">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.severity === 'HIGH' ? 'border-red-500 bg-red-500/10' :
                      alert.severity === 'MEDIUM' ? 'border-orange-500 bg-orange-500/10' :
                      'border-yellow-500 bg-yellow-500/10'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            alert.severity === 'high' || alert.severity === 'HIGH' ? 'bg-red-500' :
                            alert.severity === 'medium' || alert.severity === 'MEDIUM' ? 'bg-orange-500' :
                            'bg-yellow-500'
                          } text-white text-[10px]`}>
                            {(alert.severity || '').toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">{(alert.type || '').replace(/_/g, ' ').toUpperCase()}</Badge>
                        </div>
                      </div>
                      <p className="mt-2 text-sm">{alert.message}</p>
                      {alert.type === 'doctor_shopping' && alert.details && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Physicians: {alert.details.physician_names?.join(', ')}
                        </p>
                      )}
                      {alert.type === 'frequent_patient' && alert.details?.medications && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Medications: {alert.details.medications.join(', ')}
                        </p>
                      )}
                      {alert.type === 'overdue_pickup' && alert.details && (
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          <p>Serial #: {alert.details.serial_number} | Medication: {alert.details.medication}</p>
                          <p>Window ended: {formatDate(alert.details.window_end)} ({alert.details.days_overdue} days ago)</p>
                        </div>
                      )}
                      {alert.type === 'upcoming_pickup' && alert.details && (
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          <p>Serial #: {alert.details.serial_number} | Medication: {alert.details.medication}</p>
                          <p>Pickup window: {formatDate(alert.details.window_start)} - {formatDate(alert.details.window_end)}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Monthly Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={reportYear.toString()} onValueChange={(v) => setReportYear(parseInt(v))}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={reportMonth.toString()} onValueChange={(v) => setReportMonth(parseInt(v))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((m, idx) => (
                        <SelectItem key={idx} value={(idx + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchMonthlyReport} disabled={reportLoading}>
                  {reportLoading ? 'Loading...' : 'Generate Report'}
                </Button>
                {monthlyReport && (
                  <Button variant="outline" onClick={printReport}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print
                  </Button>
                )}
              </div>

              {monthlyReport && (
                <div className="space-y-6 print:p-4" id="monthly-report">
                  {/* Report Header */}
                  <div className="text-center border-b pb-4">
                    <h2 className="text-xl font-bold">Controlled Substance Register</h2>
                    <p className="text-muted-foreground">{monthNames[reportMonth - 1]} {reportYear}</p>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-primary">{monthlyReport.total_entries}</div>
                        <div className="text-xs text-muted-foreground">Total Entries</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-500">{monthlyReport.unique_patients}</div>
                        <div className="text-xs text-muted-foreground">Unique Patients</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-500">{monthlyReport.unique_physicians}</div>
                        <div className="text-xs text-muted-foreground">Unique Physicians</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-500">{monthlyReport.total_entries}</div>
                        <div className="text-xs text-muted-foreground">Prescriptions</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Medication Summary */}
                  {monthlyReport.medication_summary?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-2">Top Medications</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {monthlyReport.medication_summary.map((m, idx) => (
                          <div key={idx} className="p-2 bg-muted/30 rounded text-sm">
                            <span className="font-medium">{m.medication}</span>
                            <span className="text-muted-foreground ml-2">({m.count})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Entries Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead>Serial</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Physician</TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Medication</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>Treatment</TableHead>
                          <TableHead>Pickup Window</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyReport.entries?.map(entry => (
                          <TableRow key={entry.id} className="text-xs">
                            <TableCell className="font-mono">{entry.serial_number}</TableCell>
                            <TableCell>{formatDate(entry.date)}</TableCell>
                            <TableCell>{entry.physician_name}</TableCell>
                            <TableCell>{entry.patient_name}</TableCell>
                            <TableCell>{entry.medication_name}</TableCell>
                            <TableCell>{entry.quantity}</TableCell>
                            <TableCell>{entry.treatment_length_months || 1}M</TableCell>
                            <TableCell className="text-[10px]">
                              {entry.pickup_window_start && entry.pickup_window_end 
                                ? `${formatDate(entry.pickup_window_start)} - ${formatDate(entry.pickup_window_end)}`
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingPrescription ? `Edit Entry #${editingPrescription.serial_number}` : `New Entry (Serial #${nextSerialNumber})`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Physician *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowQuickAddPhysician(true)}>
                    <Plus className="w-3 h-3 mr-1" /> Add New
                  </Button>
                </Label>
                <Popover open={physicianOpen} onOpenChange={setPhysicianOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {formData.physician_id 
                        ? physicians.find(p => p.id === formData.physician_id)?.name || 'Select...'
                        : 'Search physician...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Search physician..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2 text-center">
                            <p className="text-sm text-muted-foreground">No physician found</p>
                            <Button size="sm" className="mt-2" onClick={() => { setPhysicianOpen(false); setShowQuickAddPhysician(true); }}>
                              <Plus className="w-3 h-3 mr-1" /> Add New Physician
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {physicians.map(p => (
                            <CommandItem key={p.id} value={p.name} onSelect={() => { setFormData(prev => ({ ...prev, physician_id: p.id })); setPhysicianOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.physician_id === p.id ? 'opacity-100' : 'opacity-0'}`} />
                              {p.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span>Patient *</span>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowQuickAddPatient(true)}>
                    <Plus className="w-3 h-3 mr-1" /> Add New
                  </Button>
                </Label>
                <Popover open={patientOpen} onOpenChange={setPatientOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {formData.patient_id 
                        ? patients.find(p => p.id === formData.patient_id)?.name || 'Select...'
                        : 'Search patient...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Search patient..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2 text-center">
                            <p className="text-sm text-muted-foreground">No patient found</p>
                            <Button size="sm" className="mt-2" onClick={() => { setPatientOpen(false); setShowQuickAddPatient(true); }}>
                              <Plus className="w-3 h-3 mr-1" /> Add New Patient
                            </Button>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {patients.map(p => (
                            <CommandItem key={p.id} value={p.name} onSelect={() => { setFormData(prev => ({ ...prev, patient_id: p.id })); setPatientOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.patient_id === p.id ? 'opacity-100' : 'opacity-0'}`} />
                              {p.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Medication (controlled items only)</Label>
                <Popover open={medicationOpen} onOpenChange={setMedicationOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {formData.medication_id 
                        ? controlledItems.find(i => i.id === formData.medication_id)?.name || 'Select...'
                        : 'Search medication...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput placeholder="Search controlled items..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2 text-center text-sm text-muted-foreground">
                            No controlled items found.<br/>
                            Mark items as "Controlled Substance" in Inventory.
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {controlledItems.map(item => (
                            <CommandItem key={item.id} value={item.name} onSelect={() => { handleMedicationSelect(item.id); setMedicationOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${formData.medication_id === item.id ? 'opacity-100' : 'opacity-0'}`} />
                              <div>
                                <div>{item.name}</div>
                                {item.barcode && <div className="text-xs text-muted-foreground">{item.barcode}</div>}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Medication Name *</Label>
                <Input
                  value={formData.medication_name}
                  onChange={(e) => setFormData({ ...formData, medication_name: e.target.value })}
                  placeholder="e.g., Stilnox, Xanax"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Instructions / Detail</Label>
              <Textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="Usage instructions (supports Arabic)"
                dir="auto"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Gram</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dosage_gram}
                  onChange={(e) => setFormData({ ...formData, dosage_gram: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Cent</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dosage_cent}
                  onChange={(e) => setFormData({ ...formData, dosage_cent: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Mill</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dosage_mill}
                  onChange={(e) => setFormData({ ...formData, dosage_mill: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Gtes (Drops)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.dosage_gtes}
                  onChange={(e) => setFormData({ ...formData, dosage_gtes: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label>Treatment Length *</Label>
                <Select
                  value={formData.treatment_length_months?.toString() || '1'}
                  onValueChange={(value) => setFormData({ ...formData, treatment_length_months: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Month</SelectItem>
                    <SelectItem value="2">2 Months</SelectItem>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="4">4 Months</SelectItem>
                    <SelectItem value="5">5 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prescription Image</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.prescription_image_url}
                    onChange={(e) => setFormData({ ...formData, prescription_image_url: e.target.value })}
                    placeholder="URL or upload"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
                      <span>
                        <Upload className="w-4 h-4" />
                      </span>
                    </Button>
                  </label>
                </div>
                {formData.prescription_image_url && (
                  <a href={formData.prescription_image_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                    View uploaded image
                  </a>
                )}
              </div>
            </div>
            
            {/* Pickup Window Preview */}
            {formData.date && formData.treatment_length_months && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-sm mb-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">
                    {formData.treatment_length_months > 1 
                      ? `Monthly Pickup Windows (${formData.treatment_length_months} months):`
                      : 'Expected Pickup Window:'}
                  </span>
                </div>
                <div className="space-y-1">
                  {Array.from({ length: parseInt(formData.treatment_length_months) || 1 }, (_, i) => {
                    const window = calculatePickupWindow(formData.date, i);  // Start from 0 (prescription date)
                    if (!window) return null;
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {formData.treatment_length_months > 1 && (
                          <Badge variant="secondary" className="text-[10px] w-8 justify-center">
                            {i + 1}/{formData.treatment_length_months}
                          </Badge>
                        )}
                        <span className="text-blue-600 dark:text-blue-400">
                          {formatDate(window.start)} to {formatDate(window.end)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {formData.treatment_length_months > 1 
                    ? `Creates ${formData.treatment_length_months} register entries with monthly pickup windows (±4 days)`
                    : 'Patient should return within ±4 days of the expected date'}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {editingPrescription ? 'Update' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Entry #{viewingPrescription?.serial_number}</DialogTitle>
          </DialogHeader>
          {viewingPrescription && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-muted-foreground">Date:</span> <strong>{formatDate(viewingPrescription.date)}</strong></div>
                <div><span className="text-muted-foreground">Pharmacist:</span> <strong>{viewingPrescription.pharmacist_name || '-'}</strong></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-muted-foreground">Physician:</span> <strong>{viewingPrescription.physician_name}</strong></div>
                <div><span className="text-muted-foreground">Patient:</span> <strong>{viewingPrescription.patient_name}</strong></div>
              </div>
              <div>
                <span className="text-muted-foreground">Medication:</span> <strong className="text-primary">{viewingPrescription.medication_name}</strong>
              </div>
              {viewingPrescription.instructions && (
                <div>
                  <span className="text-muted-foreground">Instructions:</span>
                  <p className="mt-1 p-2 bg-muted/30 rounded" dir="auto">{viewingPrescription.instructions}</p>
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-2 bg-muted/20 rounded">
                  <div className="text-xs text-muted-foreground">Gram</div>
                  <div className="font-mono">{viewingPrescription.dosage_gram || 0}</div>
                </div>
                <div className="p-2 bg-muted/20 rounded">
                  <div className="text-xs text-muted-foreground">Cent</div>
                  <div className="font-mono">{viewingPrescription.dosage_cent || 0}</div>
                </div>
                <div className="p-2 bg-muted/20 rounded">
                  <div className="text-xs text-muted-foreground">Mill</div>
                  <div className="font-mono">{viewingPrescription.dosage_mill || 0}</div>
                </div>
                <div className="p-2 bg-muted/20 rounded">
                  <div className="text-xs text-muted-foreground">Gtes</div>
                  <div className="font-mono">{viewingPrescription.dosage_gtes || 0}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-muted-foreground">Quantity:</span> <strong>{viewingPrescription.quantity}</strong></div>
                <div><span className="text-muted-foreground">Treatment:</span> <strong>{viewingPrescription.treatment_length_months || 1} Month(s)</strong></div>
              </div>
              {viewingPrescription.pickup_window_start && viewingPrescription.pickup_window_end && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="font-medium">Pickup Window:</span>
                      <span className="text-blue-600 dark:text-blue-400">
                        {formatDate(viewingPrescription.pickup_window_start)} - {formatDate(viewingPrescription.pickup_window_end)}
                      </span>
                    </div>
                    {(() => {
                      const status = getPickupStatus(viewingPrescription);
                      return (
                        <Badge 
                          variant={status.status === 'overdue' ? 'destructive' : 
                                  status.status === 'due' ? 'default' : 
                                  status.status === 'upcoming' ? 'secondary' : 'outline'}
                          className="w-fit"
                        >
                          {status.status === 'overdue' && `${status.daysOverdue} days overdue`}
                          {status.status === 'due' && 'Patient should pickup now'}
                          {status.status === 'upcoming' && `Pickup window opens in ${status.daysUntil} days`}
                          {status.status === 'scheduled' && `Pickup in ${status.daysUntil} days`}
                        </Badge>
                      );
                    })()}
                  </div>
                </div>
              )}
              {viewingPrescription.prescription_image_url && (
                <div className="space-y-2">
                  <span className="text-muted-foreground">Prescription Image:</span>
                  <div className="border rounded-lg overflow-hidden">
                    <img 
                      src={viewingPrescription.prescription_image_url} 
                      alt="Prescription" 
                      className="w-full max-h-[300px] object-contain bg-gray-100 dark:bg-gray-900 cursor-pointer hover:opacity-90"
                      onClick={() => window.open(viewingPrescription.prescription_image_url, '_blank')}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">Click image to view full size</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Register Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Next Serial Number</Label>
              <Input
                type="number"
                value={nextSerialNumber}
                onChange={(e) => setNextSerialNumber(parseInt(e.target.value) || 1)}
                min="1"
              />
              <p className="text-xs text-muted-foreground">Set the starting serial number for new entries</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
            <Button onClick={updateSerialNumber}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Physician Dialog */}
      <Dialog open={showQuickAddPhysician} onOpenChange={setShowQuickAddPhysician}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              Add New Physician
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Physician Name *</Label>
              <Input
                value={quickPhysicianName}
                onChange={(e) => setQuickPhysicianName(e.target.value)}
                placeholder="Dr. John Smith"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowQuickAddPhysician(false); setQuickPhysicianName(''); }}>Cancel</Button>
            <Button onClick={handleQuickAddPhysician}>
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Patient Dialog */}
      <Dialog open={showQuickAddPatient} onOpenChange={setShowQuickAddPatient}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Add New Patient
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Patient Name *</Label>
              <Input
                value={quickPatientName}
                onChange={(e) => setQuickPatientName(e.target.value)}
                placeholder="John Doe"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowQuickAddPatient(false); setQuickPatientName(''); }}>Cancel</Button>
            <Button onClick={handleQuickAddPatient}>
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrescriptionRegisterPage;
